<template>
  <div>
    <b-card no-body class="mb-0">
      <!-- Barra de filtros -->
      <div class="list-asset-model-limit-filters">
        <b-row class="filters-row mb-2">
          <b-col cols="12" md="4" class="mb-0">
            <b-form-group :label="$t('Show')" label-for="per-page-filter" class="mb-0">
              <div class="d-flex align-items-center">
                <v-select
                  id="per-page-filter"
                  v-model="perPage"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="perPageOptions"
                  :clearable="false"
                  class="per-page-selector"
                />
                <span class="ml-1 text-muted">{{ $t('entries') }}</span>
              </div>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="filters-row align-items-end">
          <b-col cols="12" md="3" class="mb-2 mb-md-0">
            <b-form-group :label="$t('Search')" label-for="search-filter" class="mb-0">
              <b-input-group>
                <b-input-group-prepend is-text>
                  <feather-icon icon="SearchIcon" size="16" />
                </b-input-group-prepend>
                <b-form-input
                  id="search-filter"
                  v-model="searchQuery"
                  :placeholder="$t('Model, parameter...')"
                  class="filter-input-search"
                />
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="3" class="mb-2 mb-md-0">
            <b-form-group :label="$t('Alert Type')" label-for="alert-type-filter" class="mb-0">
              <v-select
                id="alert-type-filter"
                v-model="alertTypeId"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="alertOptions"
                :reduce="val => val.value"
                :clearable="true"
                :placeholder="$t('All')"
                class="filter-select"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="3" class="mb-2 mb-md-0">
            <b-form-group :label="$t('Limit Type')" label-for="limit-type-filter" class="mb-0">
              <v-select
                id="limit-type-filter"
                v-model="limitTypeId"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="limitOptions"
                :reduce="val => val.value"
                :clearable="true"
                :placeholder="$t('All')"
                class="filter-select"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="3" class="d-flex align-items-end mb-2 mb-md-0 flex-wrap">
            <b-button
              v-if="hasActiveFilters"
              variant="outline-secondary"
              size="sm"
              class="mr-2 mb-1 mb-md-0"
              @click="clearFilters"
            >
              <feather-icon icon="Trash2Icon" size="14" class="mr-50" />
              {{ $t('Clear filters') }}
            </b-button>
            <b-button
              v-if="!user.role || !user.role.endsWith('VI')"
              variant="primary"
              :to="{ name: 'register-model-limit' }"
            >
              <feather-icon icon="PlusIcon" size="16" class="mr-50" />
              {{ $t('Add Asset Model Limit') }}
            </b-button>
          </b-col>
        </b-row>
      </div>

      <div class="m-2 pt-0">
      <b-table
        ref="refAssetModelLimitListTable"
        class="position-relative"
        :items="fetchSearchAssetModelLimit"
        responsive
        :fields="tableColumns"
        primary-key="id"
        show-empty
        :empty-text="emptyText"
      >
        <template #cell(assetModelName)="data">
          <span class="font-weight-bold text-nowrap">
            {{ data.item.assetModelName || data.item.AssetModelName || '-' }}
          </span>
        </template>

        <template #cell(alertType)="data">
          <span class="text-nowrap">
            {{ data.item.alertType || data.item.AlertType || '-' }}
          </span>
        </template>

        <template #cell(limitType)="data">
          <span class="text-nowrap">
            {{ data.item.limitType || data.item.LimitType || '-' }}
          </span>
        </template>

        <template #cell(telemetryParameterName)="data">
          <span class="text-nowrap">
            {{ data.item.telemetryParameterName || data.item.TelemetryParameterName || '-' }}
          </span>
        </template>

        <template #cell(value)="data">
          <span class="text-nowrap">
            {{ formatValue(data.item.value != null ? data.item.value : data.item.Value) }}
          </span>
        </template>

        <template #cell(actions)="data">
          <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
            <template #button-content>
              <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" />
            </template>
            <b-dropdown-item
              v-if="!user.role || !user.role.endsWith('VI')"
              :to="{ name: 'register-model-limit', params: { id: data.item.id || data.item.Id } }"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('Edit') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">
              {{ $t('Showing') }} {{ dataMeta.from }} {{ $t('to') }} {{ dataMeta.to }}
              {{ $t('of') }} {{ dataMeta.of }} {{ $t('entries') }}
            </span>
          </b-col>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalItems"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BFormGroup,
  BButton,
  BTable,
  BDropdown,
  BDropdownItem,
  BPagination,
  BInputGroup,
  BInputGroupPrepend,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import store from '@/store'
import { onUnmounted, computed } from '@vue/composition-api'
import useAssetModelLimitList from './useAssetModelLimitList'
import assetModelLimitStoreModule from './assetModelLimitStoreModule'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BFormGroup,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BInputGroup,
    BInputGroupPrepend,
    vSelect,
  },
  data() {
    return {
      emptyText: this.$t('No matching records found'),
    }
  },
  setup() {
    const ASSET_MODEL_LIMIT_STORE_MODULE_NAME = 'app-asset-model-limit'

    if (!store.hasModule(ASSET_MODEL_LIMIT_STORE_MODULE_NAME)) {
      store.registerModule(ASSET_MODEL_LIMIT_STORE_MODULE_NAME, assetModelLimitStoreModule)
    }

    onUnmounted(() => {
      if (store.hasModule(ASSET_MODEL_LIMIT_STORE_MODULE_NAME)) {
        store.unregisterModule(ASSET_MODEL_LIMIT_STORE_MODULE_NAME)
      }
    })

    const user = typeof localStorage !== 'undefined' && localStorage.userData
      ? JSON.parse(localStorage.userData)
      : {}

    const {
      fetchSearchAssetModelLimit,
      tableColumns,
      perPage,
      currentPage,
      totalItems,
      dataMeta,
      perPageOptions,
      searchQuery,
      refAssetModelLimitListTable,
      refetchData,
      alertTypeId,
      limitTypeId,
      alertOptions,
      limitOptions,
    } = useAssetModelLimitList()

    const formatValue = val => {
      if (val == null || val === '') return '-'
      const n = Number(val)
      return Number.isNaN(n) ? String(val) : n
    }

    const hasActiveFilters = computed(() => {
      return !!(
        (searchQuery.value && searchQuery.value.trim() !== '') ||
        alertTypeId.value != null ||
        limitTypeId.value != null
      )
    })

    const clearFilters = () => {
      searchQuery.value = ''
      alertTypeId.value = null
      limitTypeId.value = null
      refetchData()
    }

    return {
      fetchSearchAssetModelLimit,
      tableColumns,
      perPage,
      currentPage,
      totalItems,
      dataMeta,
      perPageOptions,
      searchQuery,
      refAssetModelLimitListTable,
      refetchData,
      alertTypeId,
      limitTypeId,
      alertOptions,
      limitOptions,
      user,
      formatValue,
      hasActiveFilters,
      clearFilters,
    }
  },
}
</script>

<style lang="scss" scoped>
.list-asset-model-limit-filters {
  padding: 1rem 1.25rem;
  background: #fff;
}

.filters-row {
  margin-left: -0.5rem;
  margin-right: -0.5rem;
}

.filter-input-search {
  border-left: 0;
}
.filter-input-search:focus {
  border-color: #d8d6de;
  box-shadow: none;
}

.list-asset-model-limit-filters ::v-deep .input-group-text {
  background: #fff;
  border-right: 0;
  color: #b9b9c3;
}

.list-asset-model-limit-filters ::v-deep .input-group .form-control {
  border-left: 0;
}
.list-asset-model-limit-filters ::v-deep .input-group:focus-within .input-group-text,
.list-asset-model-limit-filters ::v-deep .input-group:focus-within .form-control {
  border-color: #7367f0;
}

.filter-select {
  min-width: 0;
}

.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
